<template>
    <Popover class="absolute end-3 text-body">
      <PopoverButton>
        <span v-tippy="$t('text-insert-my-info')">
          <DocumentText class="w-6 h-6" />
        </span>
      </PopoverButton>
      <Transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="translate-y-1 opacity-0"
        enter-to-class="translate-y-0 opacity-100"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="translate-y-0 opacity-100"
        leave-to-class="translate-y-1 opacity-0"
      >
        <PopoverPanel v-slot="{ close }" class="absolute -right-2 transform bottom-10 mb-2 py-2 w-70 bg-light rounded shadow-700 z-20">
          <ul>
            <li>
              <button
                v-bind="getRootProps()"
                class="block w-full py-1 px-4 text-sm text-start font-semibold text-primary-dark transition duration-200 hover:text-accent-hover hover:underline focus:outline-none"
              >
                <input v-bind="getInputProps()" name="images" >
                <div class="flex items-center justify-start space-x-2" @click="() => closeFunc = close">
                  <ImageSolid class="w-4 h-4" />
                  <div>{{ $t('text-insert-image') }}</div>
                </div>
              </button>
            </li>
            <li v-if="auth.idSupplier && pickupAddress">
              <button
                class="block w-full py-1 px-4 text-sm text-start font-semibold text-primary-dark transition duration-200 hover:text-accent-hover hover:underline focus:outline-none"
                @click="insertPickupAddress(close)"
              >
                <div class="flex items-center justify-start space-x-2">
                  <MapPinIcon class="w-4 h-4" />
                  <div>{{ $t('text-insert-pickup-point-address') }}</div>
                </div>
              </button>
            </li>
            <li v-if="auth.idSupplier">
              <button
                class="block w-full py-1 px-4 text-sm text-start font-semibold text-primary-dark transition duration-200 hover:text-accent-hover hover:underline focus:outline-none"
                @click="insertDeliveryAddress(close)"
              >
                <div class="flex items-center justify-start space-x-2">
                  <IdentificationIcon class="w-4 h-4" />
                  <div>{{ $t('text-insert-delivery-address') }}</div>
                </div>
              </button>
            </li>
            <li v-if="auth.idCustomer">
              <button
                class="block w-full py-1 px-4 text-sm text-start font-semibold text-primary-dark transition duration-200 hover:text-accent-hover hover:underline focus:outline-none"
                @click="insertEmailAddress(close)"
              >
                <div class="flex items-center justify-start space-x-2">
                  <AtIcon class="w-4 h-4" />
                  <div>{{ $t('text-insert-email-address') }}</div>
                </div>
              </button>
            </li>
            <li v-if="auth.idSupplier">
              <button
                class="block w-full py-1 px-4 text-sm text-start font-semibold text-primary-dark transition duration-200 hover:text-accent-hover hover:underline focus:outline-none"
                @click="insertPhoneNumber(close)"
              >
                <div class="flex items-center justify-start space-x-2">
                  <PhoneIcon class="w-4 h-4" />
                  <div>{{ $t('text-insert-phone-number') }}</div>
                </div>
              </button>
            </li>
            <li v-if="auth.idSupplier">
              <button
                class="block w-full py-1 px-4 text-sm text-start font-semibold text-primary-dark transition duration-200 hover:text-accent-hover hover:underline focus:outline-none"
                @click="insertBillingAddress(close)"
              >
                <div class="flex items-center justify-start space-x-2">
                  <IdentificationSolid class="w-4 h-4" />
                  <div>{{ $t('text-insert-billing-address') }}</div>
                </div>
              </button>
            </li>
          </ul>
        </PopoverPanel>
      </Transition>
    </Popover>
</template>

<script setup>
import * as Sentry from "@sentry/vue";
import _ from 'lodash-es';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue';
import { useDropzone } from "vue3-dropzone";
import { useAuthStore } from '@stores/auth';
import { useCustomerStore } from '@stores/customer';
import DocumentText from "@components/icons/outline/document-text";
import ImageSolid from "@components/icons/solid/image-solid";
import IdentificationIcon from "@components/icons/outline/identification-icon";
import IdentificationSolid from "@components/icons/solid/identification-solid";
import AtIcon from "@components/icons/outline/at-icon";
import PhoneIcon from "@components/icons/outline/phone-icon";
import MapPinIcon from "@components/icons/map-pin-icon";

const emit = defineEmits(['update:modelValue', 'cursorChanged']);

const props = defineProps({
  modelValue: {
    type: String,
    required: true
  },
  name: {
    type: String,
    default: 'comment'
  },
  images: {
    type: Array,
    required: true
  },
  cursorPosition: {
    type: Number,
    default: 0
  },
  onChange: {
    type: Function,
    default: null
  }
});

const { t } = useI18n();
const { $eventBus } = useNuxtApp();
const auth = useAuthStore();
const customer = useCustomerStore();
const closeFunc = ref(() => {});

/**
 * Inserts text at the cursor position. Every action in this menu uses the same routine.
 */
const insertAtCursor = (text, close) => {
  const value = ` ${text} `;
  emit('update:modelValue', `${props.modelValue.slice(0, props.cursorPosition)}${value}${props.modelValue.slice(props.cursorPosition)}`);
  emit('cursorChanged', props.cursorPosition + value.length);
  close();
  $eventBus.emit('focus:input', props.name);
}

// The pickup point saved in the profile. This is a different thing from the address
// preselected in the cart: chat offers what the user marked in their profile. A removed
// point is not offered at all, because inserting an address a parcel can no longer be sent
// to is worse than hiding the action, and the profile page explains why.
const pickupAddress = computed(() => {
  const address = customer.packetaAddress?.address || null;

  return address?.pickupStatus === 'removed' ? null : address;
});

const insertBillingAddress = (close) => {
  // formatAddress already includes the company details, so they are not appended twice.
  insertAtCursor(formatAddressInline(customer.supplier?.billingAddress, t), close);
}

const insertDeliveryAddress = (close) => {
  insertAtCursor(formatAddressInline(customer.supplier?.deliveryAddress, t), close);
}

const insertPickupAddress = (close) => {
  insertAtCursor(formatAddressInline(pickupAddress.value, t), close);
}

const insertEmailAddress = (close) => {
  insertAtCursor(
    customer.supplier?.billingAddress?.email || customer.supplier?.deliveryAddress?.email || customer.email || '',
    close
  );
}

const insertPhoneNumber = (close) => {
  const callingCode = customer.supplier?.billingAddress?.calling?.callingCode || customer.supplier?.deliveryAddress?.calling?.callingCode || '';
  const phone = customer.supplier?.billingAddress?.phone || customer.supplier?.deliveryAddress?.phone || '';
  insertAtCursor(`${callingCode ? '+' + callingCode + ' ' : ''}${phone}`, close);
}

const files = ref(props.images);
const setFiles = async (data) => {
  _.map(data, (file) => {
    encodeFileContent(file, 'chat-action-menu');
    file.url = URL.createObjectURL(file);
    return file;
  });
}

watch(() => props.images, (value) => {
  if (_.isArray(value) && _.get(value, 'length', null) === 0) {
    files.value = [];
  }
});

const onDrop = (acceptedFiles) => {
  if (props.onChange) {
    props.onChange(acceptedFiles);
  }
  try {
    setFiles(acceptedFiles);
  } catch (error) {
    Sentry.captureException(error);
  }
  if (closeFunc.value) {
    closeFunc.value();
  }
}

const { getRootProps, getInputProps } = useDropzone({ onDrop, accept: 'image/*' });

const fileLoaded = async ({file, type}) => {
  if (file?.encoded && type === 'chat-action-menu') {
    props.images.push(file);
  }
}

onMounted(async () => {
  $eventBus.on('file:loaded', fileLoaded);
});

onUnmounted(() => {
  $eventBus.off('file:loaded', fileLoaded);
});

</script>
